import { getLayoutTopbarHeightPx } from '@indutrust/shared-util';

import { smoothScrollWindowTo } from '../scroll/smooth-scroll-window.util';

/** Extra gap below the sticky topbar when scrolling to / observing a section. */
const SCROLL_BUFFER_PX = 12;

/** Bottom root margin for scroll-spy — must stay in sync with the observer. */
const SCROLL_SPY_BOTTOM_ROOT_MARGIN = '60%';

/** Stable identifiers for every section rendered by the read-only factory view. */
export type FactoryViewSectionId =
  | 'description'
  | 'capabilities'
  | 'machinePark'
  | 'virtualTour'
  | 'location'
  | 'business'
  | 'documents'
  | 'media'
  | 'projectReferences'
  | 'contacts';

/** Side-nav row rendered by `FactoryViewSectionNav`. */
export type FactoryViewSectionNavItem = {
  icon: string;
  id: FactoryViewSectionId;
};

/** Prefix prepended to every `section[id]` rendered by the factory view. */
export const FACTORY_VIEW_SECTION_ANCHOR_PREFIX = 'factory-view-section';

/** Validated set of all possible section ids — used by the scroll-spy observer. */
export const ALL_VIEW_SECTION_IDS: readonly FactoryViewSectionId[] = [
  'description',
  'capabilities',
  'machinePark',
  'virtualTour',
  'location',
  'business',
  'documents',
  'media',
  'projectReferences',
  'contacts',
] as const;

/** PrimeIcons icon fragment for each view section (no `pi-` prefix). */
export const FACTORY_VIEW_SECTION_ICONS: Readonly<
  Record<FactoryViewSectionId, string>
> = {
  description: 'file-edit',
  location: 'map-marker',
  capabilities: 'wrench',
  business: 'building',
  machinePark: 'cog',
  projectReferences: 'briefcase',
  documents: 'file',
  contacts: 'users',
  media: 'images',
  virtualTour: 'eye',
};

/**
 * Returns the anchor element id for a factory view section.
 *
 * @example
 * factoryViewAnchorId('general') // "factory-view-section-general"
 */
export function factoryViewAnchorId(section: FactoryViewSectionId): string {
  return `${FACTORY_VIEW_SECTION_ANCHOR_PREFIX}-${section}`;
}

const SECTION_JUMPER_SELECTOR = '[data-testid="factory-view-section-jumper"]';

function visibleChromeHeightPx(selector: string): number {
  if (typeof document === 'undefined') {
    return 0;
  }
  const element = document.querySelector(selector);
  if (!(element instanceof HTMLElement)) {
    return 0;
  }
  if (getComputedStyle(element).display === 'none') {
    return 0;
  }
  return element.getBoundingClientRect().height;
}

/**
 * Scroll offset below sticky topbar + mobile section jumper (when visible).
 */
export function getFactoryViewScrollOffsetPx(): number {
  return (
    getLayoutTopbarHeightPx() +
    visibleChromeHeightPx(SECTION_JUMPER_SELECTOR) +
    SCROLL_BUFFER_PX
  );
}

/**
 * Returns rendered factory-view section anchors currently in the document.
 */
export function listFactoryViewSectionElements(): HTMLElement[] {
  const prefix = `${FACTORY_VIEW_SECTION_ANCHOR_PREFIX}-`;
  return Array.from(
    document.querySelectorAll<HTMLElement>(`section[id^="${prefix}"]`)
  );
}

function parseFactoryViewSectionId(
  element: HTMLElement
): FactoryViewSectionId | null {
  const prefix = `${FACTORY_VIEW_SECTION_ANCHOR_PREFIX}-`;
  const rawId = element.id.slice(prefix.length);
  return ALL_VIEW_SECTION_IDS.find((id) => id === rawId) ?? null;
}

function getFactoryViewScrollSpyZoneBottomPx(): number {
  const win = globalThis.window as Window | undefined;
  if (win === undefined) {
    return 0;
  }
  return (
    win.innerHeight *
    (1 - Number.parseFloat(SCROLL_SPY_BOTTOM_ROOT_MARGIN) / 100)
  );
}

/**
 * Returns the topmost section inside the scroll-spy observation zone, or null
 * when no section intersects the zone (e.g. hero fills the viewport on load).
 */
export function detectTopmostFactoryViewSection(): FactoryViewSectionId | null {
  const elements = listFactoryViewSectionElements();
  if (elements.length === 0) {
    return null;
  }
  const zoneTopPx = getFactoryViewScrollOffsetPx();
  const zoneBottomPx = getFactoryViewScrollSpyZoneBottomPx();
  const ranked = elements
    .filter((element) => {
      const rect = element.getBoundingClientRect();
      return rect.bottom > zoneTopPx && rect.top < zoneBottomPx;
    })
    .sort(
      (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
    );
  if (ranked.length === 0) {
    return null;
  }
  return parseFactoryViewSectionId(ranked[0]);
}

/**
 * Smooth-scrolls the document so the matching section sits below the sticky
 * topbar (and mobile jumper when present).
 * Resolves once the scroll settles (via `scrollend` or a timeout fallback).
 *
 * @example
 * await scrollToFactoryViewSection('capabilities');
 */
export async function scrollToFactoryViewSection(
  section: FactoryViewSectionId
): Promise<void> {
  const win = globalThis.window as Window | undefined;
  if (win === undefined) {
    return;
  }
  const target = document.getElementById(factoryViewAnchorId(section));
  if (!target) {
    return;
  }
  const top =
    target.getBoundingClientRect().top +
    win.scrollY -
    getFactoryViewScrollOffsetPx();
  return smoothScrollWindowTo(top);
}

/**
 * Sets up an `IntersectionObserver` against the rendered section elements and
 * calls `onActive` whenever the topmost intersecting section changes.
 *
 * Returns a no-op disconnect function when `IntersectionObserver` is
 * unavailable (SSR / test environments).
 *
 * @example
 * const disconnect = createFactoryViewSectionObserver((id) => activeSection.set(id));
 * // later …
 * disconnect();
 */
export function createFactoryViewSectionObserver(
  onActive: (section: FactoryViewSectionId) => void
): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    return () => undefined;
  }
  const prefix = `${FACTORY_VIEW_SECTION_ANCHOR_PREFIX}-`;
  const elements = listFactoryViewSectionElements();
  if (elements.length === 0) {
    return () => undefined;
  }
  // Tracks all sections currently inside the observation zone across callbacks.
  // IntersectionObserver only delivers entries that *changed* in a given batch,
  // so we must maintain the full intersecting set ourselves — otherwise an
  // exiting section clears the active item even when another section is still
  // visible.
  const intersecting = new Set<Element>();

  const topOffsetPx = getFactoryViewScrollOffsetPx();

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          intersecting.add(entry.target);
        } else {
          intersecting.delete(entry.target);
        }
      }
      if (intersecting.size === 0) {
        return;
      }
      const [topEl] = [...intersecting].sort(
        (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top
      );
      if (!(topEl instanceof HTMLElement)) {
        return;
      }
      const rawId = topEl.id.slice(prefix.length);
      const match = ALL_VIEW_SECTION_IDS.find((id) => id === rawId);
      if (match !== undefined) {
        onActive(match);
      }
    },
    {
      rootMargin: `-${topOffsetPx}px 0px -${SCROLL_SPY_BOTTOM_ROOT_MARGIN} 0px`,
      threshold: 0,
    }
  );
  for (const el of elements) {
    observer.observe(el);
  }
  return () => observer.disconnect();
}

/** Wires scroll-spy for factory view sections using store callbacks. */
export function setupFactoryViewSectionScrollSpy(store: {
  isAutoScrolling: () => boolean;
  setActiveSection: (section: FactoryViewSectionId) => void;
}): () => void {
  return createFactoryViewSectionObserver((section) => {
    if (store.isAutoScrolling()) {
      return;
    }
    store.setActiveSection(section);
  });
}

/**
 * Seeds `activeSection` when scroll-spy first connects and nothing is selected
 * yet. Prefers the section currently in the observation zone, otherwise the
 * first nav item.
 */
export function syncFactoryViewActiveSection(
  store: {
    activeSection: () => FactoryViewSectionId | null;
    isAutoScrolling: () => boolean;
    setActiveSection: (section: FactoryViewSectionId) => void;
  },
  fallbackSection: FactoryViewSectionId | null
): void {
  if (store.isAutoScrolling() || store.activeSection() !== null) {
    return;
  }
  const detected = detectTopmostFactoryViewSection();
  if (detected !== null) {
    store.setActiveSection(detected);
    return;
  }
  if (fallbackSection !== null) {
    store.setActiveSection(fallbackSection);
  }
}

/**
 * Defers scroll-spy setup until after paint, reconnecting whenever callers
 * re-invoke after nav items change. Returns handles for cleanup.
 */
export function scheduleFactoryViewSectionScrollSpy(
  store: {
    activeSection: () => FactoryViewSectionId | null;
    isAutoScrolling: () => boolean;
    setActiveSection: (section: FactoryViewSectionId) => void;
  },
  fallbackSection: FactoryViewSectionId | null
): {
  cancel: () => void;
} {
  let disconnect: (() => void) | undefined;
  let rafId: number | undefined;
  const timeoutId = setTimeout(() => {
    rafId = requestAnimationFrame(() => {
      disconnect?.();
      disconnect = setupFactoryViewSectionScrollSpy(store);
      syncFactoryViewActiveSection(store, fallbackSection);
    });
  }, 0);

  return {
    cancel: () => {
      clearTimeout(timeoutId);
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }
      disconnect?.();
    },
  };
}
